import React from "react";
import { useLocation } from "react-router-dom";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import Icon from "@material-ui/core/Icon";

// @material-ui/icons
import Face from "@material-ui/icons/Face";
import Email from "@material-ui/icons/Email";
// import LockOutline from "@material-ui/icons/LockOutline";

// core components
import CustomInput from "components/CustomInput/CustomInput.js";
import Button from "components/CustomButtons/Button.js";

import styles from "assets/jss/material-dashboard-pro-react/views/loginPageStyle.js";
import { LOGIN_SCHEME } from "../../config";
import Overlay from "../../components/Overlay/Overlay";
import {
  authenticateBasic,
  authenticate,
  completeRegister,
} from "../../store/app/actions";
import { resetPassword, updatePassword } from "../../api/auth";
import { useDispatch } from "react-redux";

import pinImg from "../../assets/img/branding/pin.svg";
import dropImg from "../../assets/img/branding/drop.svg";
import iconImg from "../../assets/img/branding/logo.png";
import wordmarkImg from "../../assets/img/branding/wordmark.png";
import "./_loginPage.scss";

const useStyles = makeStyles(styles);

export default function LoginPage() {
  const [cardAnimaton, setCardAnimation] = React.useState("cardHidden");
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const [maintMode, setMaintMode] = React.useState(null);

  React.useEffect(() => {
    let id = setTimeout(function () {
      setCardAnimation("");
    }, 700);
    // Specify how to clean up after this effect:

    if (localStorage.getItem("auth_token")) {
      setLoading(true);
      dispatch(authenticate(localStorage.getItem("auth_token"))).catch(() =>
        setLoading(false)
      );
    }
    return function cleanup() {
      window.clearTimeout(id);
    };
  }, [dispatch]);

  const [uid, setUid] = React.useState("");
  const [pwd, setPwd] = React.useState("");
  const [confirmPwd, setConfirmPwd] = React.useState("");
  const [uidError, setUidError] = React.useState(false);
  const [pwdError, setPwdError] = React.useState(false);
  const [loginError, setLoginError] = React.useState("");
  const [success, setSuccess] = React.useState(false);
  const [resetEmail, setResetEmail] = React.useState("");
  const [token, setToken] = React.useState("");

  const location = useLocation();
  const resetToken = new URLSearchParams(location.search).get("reset");
  React.useEffect(() => {
    if (location.search.includes("reset")) {
      setMaintMode("forgot_password_reset");
      setToken(resetToken);
    }
  }, []);

  const classes = useStyles();

  function handleAuthenticate() {
    dispatch(authenticateBasic(uid, pwd))
      .then((res) => {
        if (res === "init") {
          setLoginError("");
          setLoading(false);
          setSuccess(true);
          window.setTimeout(() => {
            setSuccess(false);
            setMaintMode("init");
            setUid("");
            setPwd("");
            setConfirmPwd("");
          }, 1500);
        } else {
          setLoginError("");
          setLoading(false);
          setSuccess(true);
        }
      })
      .catch((err) => {
        setLoginError(err);
        setLoading(false);
        setPwd("");
        setMaintMode("");
      });
  }

  function handleSubmit(event) {
    event.preventDefault();
    setUidError(false);
    setPwdError(false);
    let error = false;
    if (!uid) {
      setUidError(true);
      error = true;
    }
    if (!pwd) {
      setPwdError(true);
      error = true;
    }
    if (error) {
      return;
    }
    setLoading(true);
    handleAuthenticate();
  }

  const loginCard = () => {
    return (
      <form onSubmit={handleSubmit}>
        <h1>Welcome Back!</h1>
        <h4>To log in, please enter your username and password.</h4>
        {LOGIN_SCHEME === "email" && (
          <CustomInput
            labelText="Email..."
            id="uid"
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Email className={classes.inputAdornmentIcon} />
                </InputAdornment>
              ),
              onChange: (e) => setUid(e.currentTarget.value),
            }}
            error={uidError}
          />
        )}
        {LOGIN_SCHEME === "username" && (
          <CustomInput
            labelText="Username..."
            id="uid"
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              endAdornment: <Face className={classes.inputAdornmentIcon} />,
              onChange: (e) => setUid(e.currentTarget.value),
            }}
            error={uidError}
          />
        )}
        <CustomInput
          labelText="Password"
          id="password"
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Icon className={classes.inputAdornmentIcon}>lock_outline</Icon>
              </InputAdornment>
            ),
            type: "password",
            autoComplete: "off",
            onChange: (e) => setPwd(e.currentTarget.value),
            value: pwd,
            className: classes.password,
          }}
          error={pwdError}
        />
        <p
          onClick={() => {
            setMaintMode("forgot_password");
          }}
          className="forgot-password-link"
        >
          Forgot Password?
        </p>
        <div>
          <Button
            type="submit"
            color="primary"
            simple
            size="lg"
            block
            variant="contained"
          >
            Let's Go
          </Button>
        </div>
      </form>
    );
  };

  const handleSubmitResetPassword = (e) => {
    e.preventDefault();
    if (resetEmail) {
      setLoading(true);
      resetPassword(resetEmail)
        .then((res) => {
          setLoading(false);
          setMaintMode("forgot_password_success");
        })
        .catch((err) => {
          setLoading(false);
        });
    }
  };

  const forgotPasswordCard = () => {
    return (
      <form onSubmit={handleSubmitResetPassword}>
        <h1>Forgot Password</h1>
        <h4>Enter your email to reset password.</h4>

        <CustomInput
          labelText="Email..."
          id="resetEmail"
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Email className={classes.inputAdornmentIcon} />
              </InputAdornment>
            ),
            onChange: (e) => setResetEmail(e.currentTarget.value),
          }}
        />
        <div>
          <Button
            type="submit"
            color="primary"
            simple
            size="lg"
            block
            variant="contained"
          >
            Reset Password
          </Button>
        </div>
      </form>
    );
  };
  const forgotPasswordSuccessCard = () => {
    return (
      <>
        <h1>Password Reset Link Sent</h1>
        <h4>
          If there is a Stockroom account associated with the email address
          provided, an email has been sent with instructions on how to reset
          your password.
        </h4>
        <div>
          <Button
            onClick={() => setMaintMode("")}
            color="primary"
            simple
            size="lg"
            block
            variant="contained"
          >
            Back to Login
          </Button>
        </div>
      </>
    );
  };

  const handleCompleteResetPassword = (event) => {
    event.preventDefault();
    setPwdError(false);
    let error = false;
    if (!pwd) {
      setPwdError(true);
      error = true;
    }
    if (pwd !== confirmPwd) {
      setPwdError(true);
      error = true;
    }
    if (error) {
      return;
    }
    setLoading(true);
    updatePassword(token, pwd)
      .then((res) => {
        setLoginError("");
        setLoading(false);
        let token = res.data.token;
        if (token) {
          setLoading(true);
          dispatch(authenticate(token))
            .then((res) => {
              setLoginError("");
              setLoading(false);
              setSuccess(true);
              window.history.replaceState({}, document.title, "/stockroom");
            })
            .catch((error) => {
              handlePasswordResetError(error);
              setLoading(false);
              setPwd("");
              setConfirmPwd("");
            });
        }
      })
      .catch((error) => {
        handlePasswordResetError(error);
        setLoading(false);
        setPwd("");
        setConfirmPwd("");
      });
  };

  const handlePasswordResetError = (error) => {
    let messages = ["Unable to reset password."];
    if (error.response && error.response.data) {
      const responseData = error.response.data;
      if (responseData) {
        if (responseData.errors) {
          messages = responseData.errors;
        } else if (responseData.error) {
          messages = [responseData.error];
        }
      }
    }
    setLoginError(messages[0]);
  };
  const passwordResetCard = () => {
    return (
      <form onSubmit={handleCompleteResetPassword}>
        <h1>Reset Password</h1>
        <h4>Please create your new password.</h4>
        <CustomInput
          labelText="Password"
          id="password"
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Icon className={classes.inputAdornmentIcon}>lock_outline</Icon>
              </InputAdornment>
            ),
            type: "text",
            autoComplete: "off",
            onChange: (e) => setPwd(e.currentTarget.value),
            value: pwd,
            className: classes.password,
          }}
          error={pwdError}
        />
        <CustomInput
          labelText="Confirm Password"
          id="confirm_password"
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Icon className={classes.inputAdornmentIcon}>lock_outline</Icon>
              </InputAdornment>
            ),
            type: "text",
            autoComplete: "off",
            onChange: (e) => setConfirmPwd(e.currentTarget.value),
            value: confirmPwd,
            className: classes.password,
          }}
          error={pwdError}
        />

        <div>
          <Button
            type="submit"
            color="primary"
            simple
            size="lg"
            block
            variant="contained"
          >
            Reset Password
          </Button>
        </div>
      </form>
    );
  };

  const handleCompleteInit = (event) => {
    event.preventDefault();
    setUidError(false);
    setPwdError(false);
    let error = false;
    if (!uid) {
      setUidError(true);
      error = true;
    }
    if (!pwd) {
      setPwdError(true);
      error = true;
    }
    if (pwd !== confirmPwd) {
      setPwdError(true);
      setPwdError(true);
      error = true;
    }
    if (error) {
      return;
    }
    setLoading(true);

    dispatch(completeRegister(uid, pwd))
      .then((res) => {
        setLoginError("");
        setLoading(false);
        setSuccess(true);
      })
      .catch((err) => {
        setLoginError(err);
        setLoading(false);
        setUid("");
        setPwd("");
        setConfirmPwd("");
      });
  };
  const initCard = () => {
    return (
      <form onSubmit={handleCompleteInit}>
        <h1>Almost There</h1>
        <h4>Please create your username and password to continue</h4>

        {LOGIN_SCHEME === "username" && (
          <CustomInput
            labelText="Create a username..."
            id="uid"
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              endAdornment: <Face className={classes.inputAdornmentIcon} />,
              onChange: (e) => setUid(e.currentTarget.value),
              value: uid,
            }}
            error={uidError}
          />
        )}
        <CustomInput
          labelText="Password"
          id="password"
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Icon className={classes.inputAdornmentIcon}>lock_outline</Icon>
              </InputAdornment>
            ),
            type: "text",
            autoComplete: "off",
            onChange: (e) => setPwd(e.currentTarget.value),
            value: pwd,
            className: classes.password,
          }}
          error={pwdError}
        />
        <CustomInput
          labelText="Confirm Password"
          id="confirm_password"
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Icon className={classes.inputAdornmentIcon}>lock_outline</Icon>
              </InputAdornment>
            ),
            type: "text",
            autoComplete: "off",
            onChange: (e) => setConfirmPwd(e.currentTarget.value),
            value: confirmPwd,
            className: classes.password,
          }}
          error={pwdError}
        />

        <div>
          <Button
            type="submit"
            color="primary"
            simple
            size="lg"
            block
            variant="contained"
          >
            Continue
          </Button>
        </div>
      </form>
    );
  };
  return (
    <div className="container">
      <div className="branding">
        <div className="pin">
          <img src={pinImg} />
        </div>
        <div className="drop">
          <img src={dropImg} />
        </div>
        <div className="items">
          <div>
            <img src={iconImg} />
          </div>
          <div>
            <img src={wordmarkImg} />
          </div>
          <div>STOCKROOM</div>
        </div>
      </div>
      <div className="content">
        {!maintMode && loginCard()}
        {maintMode === "init" && initCard()}
        {maintMode === "forgot_password" && forgotPasswordCard()}
        {maintMode === "forgot_password_success" && forgotPasswordSuccessCard()}
        {maintMode === "forgot_password_reset" && passwordResetCard()}
        {success && <Overlay style={{ fontSize: 96 }} type="success" />}
        {loginError && (
          <Overlay
            style={{ fontSize: 96 }}
            type="error"
            message={loginError}
            dismiss={
              <Button
                color="primary"
                simple
                size="lg"
                block
                variant="contained"
                onClick={() => {
                  setLoginError(null);
                }}
              >
                Dismiss
              </Button>
            }
          />
        )}
      </div>

      {loading && <Overlay size={62} type="loading" />}
    </div>
  );
}
